import React, { useState, useEffect } from "react";
import { Box, Typography, Chip, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import JobDetailStepper from "./JobDetailStepper.js";
import axios from "axios";

const Jobs = () => {
	const [selectedJob, setSelectedJob] = useState(null);
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);

	const columns = [
		{ field: "job_id", headerName: "Job ID", width: 280 },
		{ field: "created_by", headerName: "Created By", flex: 1, minWidth: 200 },
		{ field: "job_type", headerName: "Type", width: 120 },
		{
			field: "createdAt",
			headerName: "Date Executed",
			width: 200,
			renderCell: (params) => new Date(params.value).toLocaleString(),
		},
		{
			field: "status",
			headerName: "Status",
			width: 150,
			renderCell: (params) => {
				let color = "default";
				const status = params.value?.toUpperCase();
				if (status === "SUCCESS" || status === "COMPLETED") color = "success";
				else if (status === "IN_PROGRESS" || status === "PENDING") color = "warning";
				else if (status === "FAILED" || status === "ERROR") color = "error";
				return (
					<Chip label={status} color={color} size="small" variant="outlined" sx={{ fontWeight: 600, borderRadius: "6px" }} />
				);
			},
		},
	];

	useEffect(() => {
		const fetchJobs = async () => {
			setLoading(true);
			try {
				const response = await axios.get("/api/pos_reco/get_all_job_logs");
				setRows(response.data?.result?.logs || []);
			} catch (error) {
				console.error("Failed to fetch jobs:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchJobs();
	}, []);

	// Details View
	if (selectedJob) {
		return (
			<Box
				sx={{
					width: "100%",
					p: { xs: 2, md: 4 },
					boxSizing: "border-box",
					bgcolor: "background.paper",
					borderRadius: 2,
					border: "1px solid",
					borderColor: "divider",
				}}>
				<Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
					<IconButton
						onClick={() => setSelectedJob(null)}
						sx={{ mr: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
						<ArrowBackRoundedIcon fontSize="small" />
					</IconButton>
					<Typography variant="h6" fontWeight="600" color="text.primary">
						Job Details: {selectedJob.job_type}
					</Typography>
					<Chip
						label={selectedJob.status}
						color={
							selectedJob.status?.toUpperCase() === "SUCCESS"
								? "success"
								: selectedJob.status?.toUpperCase() === "IN_PROGRESS"
									? "warning"
									: "error"
						}
						variant="outlined"
						size="small"
						sx={{ ml: 2, fontWeight: 600, borderRadius: "6px" }}
					/>
				</Box>
				<JobDetailStepper job={selectedJob} />
			</Box>
		);
	}

	// DataGrid Table View
	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 120px)",
				minHeight: 500,
				display: "flex",
				flexDirection: "column",
				p: { xs: 2, md: 4 },
				boxSizing: "border-box",
				bgcolor: "background.paper",
				borderRadius: 2,
				border: "1px solid",
				borderColor: "divider",
			}}>
			<Typography variant="h6" fontWeight="600" color="text.primary" sx={{ mb: 3 }}>
				Jobs
			</Typography>
			<Box sx={{ flexGrow: 1, width: "100%" }}>
				<DataGrid
					rows={rows}
					columns={columns}
					loading={loading}
					pageSizeOptions={[10, 25, 50]}
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
					}}
					onRowClick={(params) => setSelectedJob(params.row)}
					sx={{
						border: "none",
						"& .MuiDataGrid-columnHeaders": {
							borderBottom: "1px solid",
							borderColor: "divider",
						},
						"& .MuiDataGrid-columnHeaderTitle": {
							fontWeight: 600,
							color: "text.secondary",
							textTransform: "uppercase",
							fontSize: "0.75rem",
							letterSpacing: "0.5px",
						},
						"& .MuiDataGrid-row:hover": { cursor: "pointer", bgcolor: "action.hover" },
						"& .MuiDataGrid-cell": {
							borderBottom: "1px solid",
							borderColor: "divider",
						},
						"& .MuiDataGrid-cell:focus": { outline: "none" },
						"& .MuiDataGrid-columnHeader:focus": { outline: "none" },
					}}
				/>
			</Box>
		</Box>
	);
};

export default Jobs;
